// copied from @react-three/offscreen, since current npm version
// doesn't have the latest code changes that fix a bug related to
// rendering with the offscreencanvas instead of the fallback
import { Canvas as CanvasImpl, type RenderProps } from "@react-three/fiber";
import React, { useEffect, useRef } from "react";
import type { Options as ResizeOptions } from "react-use-measure";

const EVENTS = {
  onClick: ["click", false],
  onContextMenu: ["contextmenu", false],
  onDoubleClick: ["dblclick", false],
  onWheel: ["wheel", true],
  onPointerDown: ["pointerdown", true],
  onPointerUp: ["pointerup", true],
  onPointerLeave: ["pointerleave", true],
  onPointerMove: ["pointermove", true],
  onPointerCancel: ["pointercancel", true],
  onLostPointerCapture: ["lostpointercapture", true],
} as const;

export interface CanvasProps
  extends
    Omit<RenderProps<HTMLCanvasElement>, "size">,
    React.HTMLAttributes<HTMLDivElement> {
  worker: Worker;
  fallback?: React.ReactNode;
  /**
   * Options to pass to useMeasure.
   * @see https://github.com/pmndrs/react-use-measure#api
   */
  resize?: ResizeOptions;
  /** The target where events are being subscribed to, default: the div that wraps canvas */
  eventSource?: HTMLElement | React.MutableRefObject<HTMLElement>;
  /** The event prefix that is cast into canvas pointer x/y events, default: "offset" */
  eventPrefix?: "offset" | "client" | "page" | "layer" | "screen";
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function isRefObject<T>(ref: any): ref is React.MutableRefObject<T> {
  return ref && ref.current !== undefined;
}

export function OffscreenCanvas({
  eventSource,
  worker,
  fallback,
  style,
  className,
  id,
  ...props
}: CanvasProps) {
  const [shouldFallback, setFallback] = React.useState(false);
  const canvasRef = useRef<HTMLCanvasElement>(null!);
  const hasTransferredToOffscreen = useRef(false);

  useEffect(() => {
    if (!worker) return;

    const canvas = canvasRef.current;
    try {
      if (!hasTransferredToOffscreen.current) {
        const offscreen = canvasRef.current.transferControlToOffscreen();
        hasTransferredToOffscreen.current = true;
        worker.postMessage(
          {
            type: "init",
            payload: {
              props,
              drawingSurface: offscreen,
              width: canvas.clientWidth,
              height: canvas.clientHeight,
              top: canvas.offsetTop,
              left: canvas.offsetLeft,
              pixelRatio: window.devicePixelRatio,
            },
          },
          [offscreen],
        );
      }
    } catch {
      // Browser doesn't support offscreen canvas at all
      setFallback(true);
      return;
    }

    worker.onmessage = (e) => {
      if (e.data.type === "error") {
        // Worker failed to initialize
        setFallback(true);
      }
    };

    const currentEventSource = isRefObject(eventSource)
      ? eventSource.current
      : eventSource || canvas;

    Object.values(EVENTS).forEach(([eventName, passive]) => {
      currentEventSource.addEventListener(
        eventName,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (event: any) => {
          // Prevent default for all passive events
          if (!passive) event.preventDefault();
          // Capture pointer automatically on pointer down
          if (eventName === "pointerdown") {
            event.target.setPointerCapture(event.pointerId);
          } else if (eventName === "pointerup") {
            event.target.releasePointerCapture(event.pointerId);
          }

          worker.postMessage({
            type: "dom_events",
            payload: {
              eventName,
              deltaX: event.deltaX,
              deltaY: event.deltaY,
              pointerId: event.pointerId,
              pointerType: event.pointerType,
              button: event.button,
              buttons: event.buttons,
              altKey: event.altKey,
              ctrlKey: event.ctrlKey,
              metaKey: event.metaKey,
              shiftKey: event.shiftKey,
              movementX: event.movementX,
              movementY: event.movementY,
              clientX: event.clientX,
              clientY: event.clientY,
              offsetX: event.offsetX,
              offsetY: event.offsetY,
              pageX: event.pageX,
              pageY: event.pageY,
              x: event.x,
              y: event.y,
            },
          });
        },
        { passive },
      );
    });

    const handleResize = () => {
      worker.postMessage({
        type: "resize",
        payload: {
          width: currentEventSource.clientWidth,
          height: currentEventSource.clientHeight,
          top: currentEventSource.offsetTop,
          left: currentEventSource.offsetLeft,
        },
      });
    };

    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [eventSource, props, worker]);

  useEffect(() => {
    if (!worker) return;
    worker.postMessage({ type: "props", payload: props });
  }, [worker, props]);

  return shouldFallback ? (
    <CanvasImpl id={id} className={className} style={style} {...props}>
      {fallback}
    </CanvasImpl>
  ) : (
    <canvas
      id={id}
      className={className}
      style={{
        position: "relative",
        width: "100%",
        height: "100%",
        overflow: "hidden",
        display: "block",
        ...style,
      }}
      ref={canvasRef}
    />
  );
}
